/**
 * Pagina /admin/tenencias — Sprint 13 S13-07 (migrada al portal
 * bajo Vista Comision desde apps/frontend-superadmin tras la
 * doctrina Portal vs Superadmin del 2026-05-13).
 *
 * Original: Sprint 12 S12-06. Listado de tenencias activas con
 * accion "Cerrar tenencia" por fila. El cierre se hace via
 * ModalCerrarTenencia (form con fecha + motivo + confirmacion
 * explicita). Consume POST /tenencias/{id}/cerrar de S11-V2-13.
 *
 * RBAC: ROLES_ADMIN ('comision' o 'tesorero') + tipo_cuenta='operativa'
 * (gate de dominio S13-01). El backend tambien ajustado a
 * require_comision_o_tesorero en S13-07. Cerrar tenencias es flujo
 * ex-vecino — el rol comision lo opera de la mano con la directiva.
 */
import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../lib/api/client';
import { formatRut } from '../../lib/format';
import { listTenencias } from '../../lib/api/tenencias';
import {
  getHistorialLoteCompleto,
  type EventoHistorial,
} from '../../lib/api/lotes';
import type { TenenciaConPersonaLote } from '../../lib/api/types';
import { ETIQUETA_TIPO_TENENCIA } from '../../lib/api/types';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { HistorialTimeline } from '../../components/HistorialTimeline';
import { ModalCerrarTenencia } from './ModalCerrarTenencia';

type FiltroEstado = 'activas' | 'cerradas' | 'todas';

function formatFechaCorta(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const [, anio, mes, dia] = m;
  return `${dia}-${mes}-${anio}`;
}

function FilaTenencia({
  tenencia,
  onCerrar,
  onVerHistorial,
}: {
  tenencia: TenenciaConPersonaLote;
  onCerrar: (t: TenenciaConPersonaLote) => void;
  onVerHistorial: (t: TenenciaConPersonaLote) => void;
}) {
  const esActiva = tenencia.hasta === null;
  const filaBg = esActiva ? '' : 'bg-bg-sunken/40';
  return (
    <tr
      className={`border-b-[0.5px] border-white/[0.08] ${filaBg}`}
      data-testid="fila-tenencia"
    >
      <td className="px-4 py-3 text-sm">
        <div className="font-medium text-text-primary">
          {tenencia.lote_sector} {tenencia.lote_identificador}
        </div>
      </td>
      <td className="px-4 py-3 text-sm">
        <div className="text-text-primary">{tenencia.persona_nombre_completo}</div>
        <div className="text-xs text-text-muted">{formatRut(tenencia.persona_rut)}</div>
      </td>
      <td className="px-4 py-3 text-sm text-text-secondary">
        {ETIQUETA_TIPO_TENENCIA[tenencia.tipo] ?? tenencia.tipo}
      </td>
      <td className="px-4 py-3 text-sm text-text-secondary">
        {formatFechaCorta(tenencia.desde)}
      </td>
      <td className="px-4 py-3 text-sm">
        {tenencia.hasta ? (
          <div>
            <div className="text-text-secondary">{formatFechaCorta(tenencia.hasta)}</div>
            {tenencia.motivo_cierre && (
              <div className="text-xs text-text-muted italic">{tenencia.motivo_cierre}</div>
            )}
          </div>
        ) : (
          <span className="inline-flex items-center px-2 py-[2px] bg-state-success/20 text-state-success rounded-xs text-xs font-medium">
            Activa
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        {!esActiva && (
          <span className="text-xs text-text-muted mr-2">Cerrada</span>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onVerHistorial(tenencia)}
        >
          Historial
        </Button>
        {esActiva && (
          <Button variant="ghost" size="sm" onClick={() => onCerrar(tenencia)}>
            Cerrar tenencia →
          </Button>
        )}
      </td>
    </tr>
  );
}

export function TenenciasPage() {
  const [tenencias, setTenencias] = useState<TenenciaConPersonaLote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtroEstado, setFiltroEstado] = useState<FiltroEstado>('activas');
  const [refreshKey, setRefreshKey] = useState(0);
  const [tenenciaSeleccionada, setTenenciaSeleccionada] =
    useState<TenenciaConPersonaLote | null>(null);
  // Sprint 18 P7: historial del lote (vista comisión, completo).
  const [historialLote, setHistorialLote] =
    useState<TenenciaConPersonaLote | null>(null);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);
    const filtros =
      filtroEstado === 'activas'
        ? { activas: true }
        : filtroEstado === 'cerradas'
        ? { activas: false }
        : {};
    listTenencias(filtros)
      .then((data) => {
        if (!cancelado) setTenencias(data);
      })
      .catch((err) => {
        if (cancelado) return;
        const mensaje =
          err instanceof ApiError
            ? `Error ${err.status} al cargar tenencias`
            : 'Error de red';
        setError(mensaje);
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [filtroEstado, refreshKey]);

  const stats = useMemo(() => {
    const total = tenencias.length;
    const activas = tenencias.filter((t) => t.hasta === null).length;
    const cerradas = total - activas;
    return { total, activas, cerradas };
  }, [tenencias]);

  function handleCerrar(t: TenenciaConPersonaLote) {
    setTenenciaSeleccionada(t);
  }

  function handleTenenciaCerrada() {
    setTenenciaSeleccionada(null);
    setRefreshKey((k) => k + 1);
  }

  return (
    <div className="px-8 py-6 space-y-6">
      <div>
        <div className="text-xs uppercase tracking-wide text-admin-red font-medium">
          Super admin · Acceso técnico
        </div>
        <h1 className="text-2xl font-medium text-text-primary mt-1">
          Gestión de tenencias
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          Cerrar la tenencia de un vecino que deja el barrio. Si la persona
          queda sin otras tenencias activas, se marca como ex-vecino
          automáticamente (no recibe más comunicaciones operativas).
        </p>
      </div>

      <div className="flex gap-3 flex-wrap items-center">
        <span className="text-sm text-text-secondary">Filtrar:</span>
        {(['activas', 'cerradas', 'todas'] as FiltroEstado[]).map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => setFiltroEstado(opt)}
            aria-pressed={filtroEstado === opt}
            className={
              filtroEstado === opt
                ? 'px-3 py-1 bg-admin-red/15 text-admin-red rounded-full text-sm font-medium cursor-pointer'
                : 'px-3 py-1 bg-bg-sunken text-text-secondary rounded-full text-sm cursor-pointer hover:text-text-primary'
            }
          >
            {opt.charAt(0).toUpperCase() + opt.slice(1)}
          </button>
        ))}
        <span className="text-sm text-text-muted ml-auto">
          {stats.total} tenencia{stats.total === 1 ? '' : 's'} ·{' '}
          {stats.activas} activa{stats.activas === 1 ? '' : 's'} ·{' '}
          {stats.cerradas} cerrada{stats.cerradas === 1 ? '' : 's'}
        </span>
      </div>

      {loading && (
        <p className="text-sm text-text-muted">Cargando tenencias…</p>
      )}
      {error && !loading && (
        <div
          className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
          role="alert"
        >
          {error}
        </div>
      )}
      {!loading && !error && tenencias.length === 0 && (
        <p className="text-sm text-text-muted">No hay tenencias para el filtro elegido.</p>
      )}
      {!loading && !error && tenencias.length > 0 && (
        <div className="bg-bg-card border-[0.5px] border-white/[0.08] rounded-md overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b-[0.5px] border-white/15 bg-bg-sunken">
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-text-secondary font-medium">
                  Casa
                </th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-text-secondary font-medium">
                  Persona
                </th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-text-secondary font-medium">
                  Vínculo
                </th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-text-secondary font-medium">
                  Desde
                </th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-text-secondary font-medium">
                  Estado
                </th>
                <th className="text-right px-4 py-2 text-xs uppercase tracking-wide text-text-secondary font-medium">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {tenencias.map((t) => (
                <FilaTenencia
                  key={t.id}
                  tenencia={t}
                  onCerrar={handleCerrar}
                  onVerHistorial={setHistorialLote}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tenenciaSeleccionada && (
        <ModalCerrarTenencia
          tenencia={tenenciaSeleccionada}
          onCerrar={() => setTenenciaSeleccionada(null)}
          onCerrada={handleTenenciaCerrada}
        />
      )}

      {historialLote && (
        <ModalHistorialLote
          loteId={historialLote.lote_id}
          loteLabel={`${historialLote.lote_sector} ${historialLote.lote_identificador}`}
          onCerrar={() => setHistorialLote(null)}
        />
      )}
    </div>
  );
}

function ModalHistorialLote({
  loteId,
  loteLabel,
  onCerrar,
}: {
  loteId: string;
  loteLabel: string;
  onCerrar: () => void;
}) {
  const [eventos, setEventos] = useState<EventoHistorial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    getHistorialLoteCompleto(loteId)
      .then((data) => {
        if (!cancelado) setEventos(data);
      })
      .catch(() => {
        if (!cancelado) setError('No pudimos cargar el historial.');
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [loteId]);

  return (
    <Modal title={`Historial — ${loteLabel}`} onClose={onCerrar}>
      {loading ? (
        <p className="text-sm text-text-muted">Cargando…</p>
      ) : error ? (
        <p className="text-sm text-state-danger" role="alert">
          {error}
        </p>
      ) : (
        <HistorialTimeline eventos={eventos} />
      )}
    </Modal>
  );
}
